import React, { useMemo, useCallback } from 'react'
import classNames from 'classnames'
import { shortenText } from '@/util/textFormat'
import Dropdown from '@/components/ui/dropdown/Dropdown'
import { i18n } from '@/locale/i18n'
import TabbedContent from '@/components/tabbed-content/TabbedContent'
import ArrowUp from '@/assets/icons/arrowUp'
import ArrowDown from '@/assets/icons/arrowDown'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi } from '@/features/market/utils/marketType'
import constants from '@/constants'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import Thumbnail from '@/components/ui/thumbnail'
  
const OutcomeSelector = React.memo(({
  question,
  outcome,
  positionType = constants.position.LONG,
  wagerSelected = null,
  onOutcomeChange = () => {},
  onPositionChange = () => {},
  constants = constants,
  i18nYes = 'Yes',
  i18nNo = 'No',
  i18nSelectBet = 'Select an outcome',
  formatNumber = (value) => value?.toString() || '-',
  currencies = {}, 
  CurrencyIcon = null,
  variant = 'buttons', // 'buttons' | 'tabs'
}) => {
  const marketUi = useMemo(() => deriveMarketUi(question), [question])
  const monoSingle = marketUi.isMono
  const binaryLabelStyle = marketUi.binaryLabelStyle

  // Helper function to check if outcome/position is highlighted - memoized
  const isHighlighted = useCallback(
    (outcomeId, _position) => {
      const isBinaryQuestion = Boolean(binaryLabelStyle)
      if (isBinaryQuestion && !monoSingle) {
        return outcome && outcome?.id === outcomeId
      }
      return positionType === _position
    },
    [binaryLabelStyle, monoSingle, outcome, positionType]
  )

  // Helper function to render position text for multi-outcome questions - memoized
  const renderPosition = useCallback((pos) => {
    return (
      <>
        <span className={pos === constants.position.SHORT ? '!text-[#ED0071]' : '!text-[var(--color-brand-04)]'}>
          {pos === constants.position.LONG ? i18nYes : i18nNo}
        </span> 
        {' on'}
      </>
    )
  }, [i18nYes, i18nNo])

  // Memoize filtered wagers for performance
  const pendingWagers = useMemo(() => {
    if (!question?.wagers) return []
    return question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE)
  }, [question?.wagers])

  // Memoize sellable outcome list
  const sellableOutcomeList = useMemo(() => {
    if (!question || pendingWagers.length === 0) {
      return [
        {
          value: null,
          text: (
            <div className="flex flex-col justify-center items-center w-full gap-2">
              <span className="font-bold">No Positions purchased yet</span>
              <span className="!text-Neutral-4 text-xs">No Positions purchased yet</span>
            </div>
          ),
        },
      ]
    }

    return pendingWagers
      .map(({ outcome: wagerOutcome, ...wager }) => {
        // This would normally calculate available shares, but for UI component we'll use placeholder
        const wagerAvailableShares = wager.shares || 0

        return {
          value: wager.id,
          wagerAvailableShares,
          text: (
            <div className="flex gap-4 items-center">
              <div className="relative">
                <Thumbnail
                  image={wagerOutcome.thumbnail}
                  alt={wagerOutcome.title}
                  className="rounded w-8 h-8"
                  variant="rect"
                />
                {CurrencyIcon && currencies[wager.currency] && (
                  <i className="absolute left-0 bottom-0 z-10">
                    <CurrencyIcon currency={currencies[wager.currency]} width="13px" />
                  </i>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-Neutral-6">
                  {question.outcomes.length > 2 ? (
                    <>
                      {renderPosition(wager.position)} {shortenText(wagerOutcome.title, 17)}
                    </>
                  ) : (
                    shortenText(wagerOutcome.title, 17)
                  )}
                </div>
                <span className="text-Neutral-4 text-xs leading-none">
                  {wagerAvailableShares &&
                    formatNumber(
                      wagerAvailableShares,
                      currencies[wager.currency]?.display_decimal_places || 2,
                      currencies[wager.currency]?.display_decimal_places || 2
                    )}{' '}
                  available shares
                  {wagerAvailableShares === 0 && <span className="text-orange-500"> (all committed to orders)</span>}
                </span>
              </div>
            </div>
          ),
        }
      })
      .filter(item => item.wagerAvailableShares > 0)
  }, [question, pendingWagers, renderPosition, formatNumber, currencies, CurrencyIcon])

  // Memoize sellable outcome change handler
  const handleSellableOutcomeChange = useCallback((id) => {
    const selectedWager = question.wagers.find(w => w.id === id)
    if (selectedWager) {
      onOutcomeChange(selectedWager.outcome)
    }
  }, [question?.wagers, onOutcomeChange])

  // Render sellable outcome selector for sell mode - memoized
  const sellableOutcomeSelector = useMemo(() => {
    return (
      <Dropdown
        type="outline"
        dropdownType="list"
        placeholder={i18nSelectBet}
        value={wagerSelected && wagerSelected.id}
        list={sellableOutcomeList}
        arrow="outlined"
        singleOptionStyle={wagerSelected ? 'tick' : null}
        className="market-filter"
        onChange={handleSellableOutcomeChange}
      />
    )
  }, [i18nSelectBet, wagerSelected, sellableOutcomeList, handleSellableOutcomeChange])

  // Memoize major outcome image for Yes/No questions
  const majorOutcomeImage = useMemo(() => {
    const outcomes = question.outcomes
    const majorOutcome = outcomes?.[0]
    if (majorOutcome && binaryLabelStyle) {
      return (
        <Thumbnail
          className="w-10 h-10 rounded-md"
          image={majorOutcome.thumbnail}
          alt={majorOutcome.title}
          variant="rect"
        />
      )
    }
    return null
  }, [question.outcomes, binaryLabelStyle])

  const handleYesClick = useCallback(() => {
    const isBinaryQuestion = Boolean(binaryLabelStyle)
    if (monoSingle && isBinaryQuestion) {
      const singleOutcome = question.outcomes[0]
      if (!outcome || outcome.id !== singleOutcome?.id) {
        onOutcomeChange(singleOutcome)
      }
      onPositionChange(constants.position.LONG)
    } else if (isBinaryQuestion) {
      onOutcomeChange(question.outcomes[0])
    } else {
      onPositionChange(constants.position.LONG)
    }
  }, [binaryLabelStyle, monoSingle, question, outcome?.id, onOutcomeChange, onPositionChange, constants.position.LONG])

  const handleNoClick = useCallback(() => {
    const isBinaryQuestion = Boolean(binaryLabelStyle)
    if (monoSingle && isBinaryQuestion) {
      const singleOutcome = question.outcomes[0]
      if (!outcome || outcome.id !== singleOutcome?.id) {
        onOutcomeChange(singleOutcome)
      }
      onPositionChange(constants.position.SHORT)
    } else if (isBinaryQuestion) {
      onOutcomeChange(question.outcomes[1])
    } else {
      onPositionChange(constants.position.SHORT)
    }
  }, [binaryLabelStyle, monoSingle, question, outcome?.id, onOutcomeChange, onPositionChange, constants.position.SHORT])

  // Memoize yes/no button classes
  const yesButtonClasses = useMemo(() => {
    return classNames(
      'flex justify-center items-center h-12 px-4 md:px-8 w-full rounded-[var(--radius-sm)] transition-all duration-200',
      'bg-positive-5/30 text-Neutral-6 hover:bg-positive-5/50 cursor-pointer',
      {
        '': !isHighlighted(question.outcomes?.[0]?.id, constants.position.LONG),
        'font-bold !bg-positive-3 !text-white': isHighlighted(question.outcomes?.[0]?.id, constants.position.LONG),
        '!bg-Neutral-3 !text-Neutral-4': isHighlighted(question.outcomes?.[1]?.id, constants.position.SHORT),
      }
    )
  }, [isHighlighted, question.outcomes, constants.position.LONG, constants.position.SHORT])

  const noButtonClasses = useMemo(() => {
    return classNames(
      'flex justify-center items-center h-12 px-4 md:px-8 w-full rounded-[var(--radius-sm)] transition-all duration-200',
      'bg-auxiliaries-3/30 text-Neutral-6 hover:bg-auxiliaries-3/50 cursor-pointer',
      {
        'font-bold !bg-auxiliaries-3 !text-white': isHighlighted(
          question.outcomes?.[1]?.id,
          constants.position.SHORT
        ),
        '!bg-Neutral-3 !text-Neutral-4': isHighlighted(question.outcomes?.[0]?.id, constants.position.LONG),
      }
    )
  }, [isHighlighted, question.outcomes, constants.position.SHORT, constants.position.LONG])

  // Binary buttons (Yes/No or Up/Down) - memoized
  const yesNoButtons = useMemo(() => {
    const isUpDownQuestion = binaryLabelStyle === 'updown'
    
    // Get labels from outcome (long_label/short_label) instead of hardcoded Yes/No
    // Use i18nYes/i18nNo if provided (from parent), otherwise get from outcome
    const positionLabels = i18nYes !== 'Yes' && i18nNo !== 'No' 
      ? { longLabel: i18nYes, shortLabel: i18nNo }
      : getPositionLabels(outcome || question?.outcomes?.[0], question)
    const firstLabel = positionLabels.longLabel
    const secondLabel = positionLabels.shortLabel
    
    return (
      <div className="flex gap-2">
        <button onClick={handleYesClick} className={yesButtonClasses}>
          <div className="flex items-center gap-2">
            {isUpDownQuestion && <ArrowUp width="16" height="16" />}
            <span className="text-lg font-medium">{firstLabel}</span>
          </div>
        </button>
        <button onClick={handleNoClick} className={noButtonClasses}>
          <div className="flex items-center gap-2">
            {isUpDownQuestion && <ArrowDown width="16" height="16" />}
            <span className="text-lg font-medium">{secondLabel}</span>
          </div>
        </button>
      </div>
    )
  }, [handleYesClick, handleNoClick, yesButtonClasses, noButtonClasses, i18nYes, i18nNo, question, outcome])

  const binaryTabs = useMemo(() => {
    const yesLabel = `${i18n('place_bet.trade')} ${i18nYes}`
    const noLabel = `${i18n('place_bet.trade')} ${i18nNo}`
    return [
      { label: yesLabel, render: () => <div /> },
      { label: noLabel, render: () => <div /> },
    ]
  }, [i18nYes, i18nNo])

  const activeBinaryTabIndex = useMemo(() => {
    const isBinaryQuestion = Boolean(binaryLabelStyle)

    if (isBinaryQuestion && !monoSingle) {
      if (outcome && outcome?.id === question.outcomes?.[0]?.id) return 0
      if (outcome && outcome?.id === question.outcomes?.[1]?.id) return 1
      return 0
    }
    return positionType === constants.position.LONG ? 0 : 1
  }, [binaryLabelStyle, question, outcome?.id, monoSingle, positionType, constants.position.LONG])

  const handleBinaryTabChange = useCallback((idx) => {
    if (idx === 0) handleYesClick()
    else handleNoClick()
  }, [handleYesClick, handleNoClick])

  // Memoize buy outcome list for multiple choice questions
  const buyOutcomeList = useMemo(() => {
    if (!question?.outcomes) return []
    
    return question.outcomes.map(o => ({
      value: o.id,
      text: (
        <div className="flex gap-2 items-center">
          <Thumbnail
            image={o.thumbnail}
            alt={o.title}
            className="rounded w-8 h-8"
            variant="rect"
          />
          {shortenText(o.title, 17)}
        </div>
      ),
    }))
  }, [question?.outcomes])

  // Memoize buy outcome change handler
  const handleBuyOutcomeChange = useCallback((id) => {
    const selectedOutcome = question.outcomes.find(o => o.id === id)
    onOutcomeChange(selectedOutcome)
  }, [question?.outcomes, onOutcomeChange])

  // Render main outcome selector for buy mode - memoized
  const buyOutcomeSelector = useMemo(() => {
    const isBinaryQuestion = Boolean(binaryLabelStyle)

    // For binary questions (Yes/No or Up/Down)
    if (isBinaryQuestion) {
      return (
        <div className="flex items-center justify-between w-full gap-4">
          <div className="hidden md:!flex items-center gap-2">
            {majorOutcomeImage}
            <h3 className="text-md font-medium text-Neutral-6">{question.title}</h3>
          </div>
          <div className="w-full md:!w-auto md:flex-shrink-0">{yesNoButtons}</div>
        </div>
      )
    }

    // For multiple choice questions
    return (
      <>
        <Dropdown
          type="outline"
          dropdownType="list"
          placeholder={i18nSelectBet}
          value={outcome && outcome.id}
          list={buyOutcomeList}
          arrow="outlined"
          singleOptionStyle="tick"
          className="market-filter"
          onChange={handleBuyOutcomeChange}
        />
        {question.outcomes.length > 2 && yesNoButtons}
      </>
    )
  }, [binaryLabelStyle, question.title, question.outcomes, majorOutcomeImage, yesNoButtons, i18nSelectBet, outcome, buyOutcomeList, handleBuyOutcomeChange])

  // Main render
  if (wagerSelected) {
    return sellableOutcomeSelector
  }

  if (variant === 'tabs') {
    return (
      <TabbedContent
        activeIndex={activeBinaryTabIndex}
        contents={binaryTabs}
        className="orderbook-tabs text-base"
        onContentChange={handleBinaryTabChange}
      />
    )
  }

  return buyOutcomeSelector
})

OutcomeSelector.displayName = 'OutcomeSelector'

export default OutcomeSelector 
